/**
 * Zod schema for the create persona form.
 * Single source of truth for types, validation, and step configuration.
 */
import type { PersonaRequestDto } from '@inference.engine/api-client/web';
import { z } from 'zod';
import type { TFunction } from 'i18next';

import {
  type SpaceVisibilityValue,
  spaceVisibilityValues,
} from '../PersonaFormSections/SpaceVisibilityField';

import { i18n } from '@/shared/i18n';
import { normalizeGender } from '../../lib';

const buildBasePersonaSchema = (t: TFunction<'validation'>) =>
  z.object({
    // Step 1: Profile Details
    // Basic Information
    age: z.string().min(1, t('audiences.createPersonaForm.ageIsRequired')),
    gender: z.string(),

    // Location
    country: z.string().min(1, t('audiences.createPersonaForm.countryIsRequired')),
    state: z.string(),
    city: z.string(),
    profession: z.string().min(1, t('audiences.createPersonaForm.professionIsRequired')),

    // Demographics
    annualIncome: z.string(),
    familyComposition: z.string(),

    // Communication Preference
    toneOfVoice: z.array(z.string()),

    // Segment Details
    segmentName: z.string().min(1, t('audiences.createPersonaForm.segmentIsRequired')),
    segmentDescription: z
      .string()
      .min(1, t('audiences.createPersonaForm.segmentDescriptionIsRequired')),
    topicsOfInterest: z.array(z.string()),

    // Custom Requirements
    customRequirement: z.string(),

    // GWI audience link (optional, applied via follow-up patch after create)
    gwiId: z
      .uuid(t('audiences.createPersonaForm.mustBeAValidGwiAudienceUuid'))
      .or(z.literal(''))
      .nullable()
      .optional(),

    // Step 2: Space Visibility
    spaceVisibility: z
      .enum(spaceVisibilityValues)
      .nullable()
      .refine((val): val is SpaceVisibilityValue => val !== null, {
        message: t('audiences.createPersonaForm.spaceVisibilityIsRequired'),
      }),

    workspaceIds: z.array(z.uuid()),
  });

// A factory, not a module-scope const: building the schema at import time would bake
// in whatever language was active then, and the messages could never follow a change.
export function buildCreatePersonaSchema(t: TFunction<'validation'>) {
  // A persona targeted at specific workspaces with none picked would reach the API as a
  // request it rejects, so the form has to catch it first.
  return buildBasePersonaSchema(t).superRefine((data, ctx) => {
    if (data.spaceVisibility === 'workspaces' && data.workspaceIds.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['workspaceIds'],
        message: t('audiences.createPersonaForm.pickAtLeastOneWorkspace'),
      });
    }
  });
}

/**
 * Form data type - uses input type to allow null before validation.
 */
export type CreatePersonaFormData = z.input<ReturnType<typeof buildBasePersonaSchema>>;

interface FormStep {
  id: string;
  label: string;
  requiredFields: readonly (keyof CreatePersonaFormData)[];
}

export const FORM_STEPS = {
  personaInformation: {
    id: 'persona-information',
    get label() {
      return i18n.t('audiences:audiences.createPersonaForm.fillPersonaInformation');
    },
    requiredFields: ['age', 'country', 'profession', 'segmentName', 'segmentDescription'] as const,
  },
  spaceVisibility: {
    id: 'space-visibility',
    get label() {
      return i18n.t('audiences:audiences.createPersonaForm.setSpaceVisibility');
    },
    requiredFields: ['spaceVisibility', 'workspaceIds'] as const,
  },
} satisfies Record<string, FormStep>;

export const FORM_STEP_ARRAY = Object.values(FORM_STEPS);

/**
 * All required fields - derived from step configuration.
 * Used for visual indicators (asterisk) on form fields.
 */
export const REQUIRED_FIELDS: (keyof CreatePersonaFormData)[] = FORM_STEP_ARRAY.flatMap(
  (step) => step.requiredFields,
);

export function isFieldRequired(fieldName: keyof CreatePersonaFormData): boolean {
  return REQUIRED_FIELDS.includes(fieldName);
}

export const defaultFormData: CreatePersonaFormData = {
  age: '',
  gender: '',
  country: '',
  state: '',
  city: '',
  profession: '',
  annualIncome: '',
  familyComposition: '',
  toneOfVoice: [],
  segmentName: '',
  segmentDescription: '',
  topicsOfInterest: [],
  customRequirement: '',
  gwiId: null,
  spaceVisibility: null,
  workspaceIds: [],
};

function mapGender(gender: string): 'male' | 'female' | 'unknown' | undefined {
  const normalized = normalizeGender(gender);
  if (normalized === 'male' || normalized === 'female') return normalized;
  if (normalized) return 'unknown';
  return undefined;
}

/**
 * Collapses the form's three choices onto the two the API has: both workspace choices send
 * `spaceVisibility: 'workspace'`, told apart by whether a `workspaceIds` list rides along.
 */
function mapSpaceVisibility(
  value: SpaceVisibilityValue | null,
): 'workspace' | 'organization' | undefined {
  if (value === 'workspaces') {
    return 'workspace';
  }
  if (value === 'workspace' || value === 'organization') {
    return value;
  }
  return undefined;
}

export function mapFormDataToCreatePayload(formData: CreatePersonaFormData): PersonaRequestDto {
  return {
    age: formData.age,
    gender: mapGender(formData.gender),
    country: formData.country,
    state: formData.state || undefined,
    city: formData.city || undefined,
    profession: formData.profession,
    annualIncome: formData.annualIncome || undefined,
    familyComposition: formData.familyComposition || undefined,
    toneOfVoice: formData.toneOfVoice.length > 0 ? formData.toneOfVoice : undefined,
    segmentName: formData.segmentName,
    segmentDescription: formData.segmentDescription,
    topicOrBrandOfInterest:
      formData.topicsOfInterest.length > 0 ? formData.topicsOfInterest : undefined,
    customRequirement: formData.customRequirement || undefined,
    spaceVisibility: mapSpaceVisibility(formData.spaceVisibility),
    workspaceIds:
      formData.spaceVisibility === 'workspaces' && formData.workspaceIds.length > 0
        ? formData.workspaceIds
        : undefined,
    gwiId: formData.gwiId?.trim() || undefined,
  };
}
